import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Paper, Chip, Skeleton, Snackbar, Alert, TextField, FormControl, InputLabel, Select, MenuItem, Button, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, Menu } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ReportService from '../../../api/services/reportService';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import ReplayIcon from '@mui/icons-material/Replay';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { formatCurrency } from '../../../utils/currency';
import { standardFilterInputSx, standardFilterSelectSx, standardResetButtonSx, standardSecondaryButtonSx } from '../../../components/common/tableStyles';

export default function CancellationReportTab({ tenantId, isActive }) {
    const [cancelData, setCancelData] = useState([]);
    const [cancelSummary, setCancelSummary] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        startDate: '',
        endDate: '',
        period: 'daily',
        bookingSource: ''
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [isExporting, setIsExporting] = useState(false);

    useEffect(() => {
        if (isActive) {
            fetchCancelData();
        }
    }, [isActive, tenantId, filters, page, rowsPerPage]);

    const fetchCancelData = async (isExport = false) => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { ...filters };
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null || params[key] === undefined) delete params[key];
            });
            
            if (isExport) {
                params.noPaging = true;
            } else {
                params.page = page + 1;
                params.limit = rowsPerPage;
            }
            
            const res = await ReportService.getCancellationReport(tenantId, params);
            const reportData = res?.data || res;
            setCancelSummary(reportData || null);
            setCancelData(reportData?.cancellations || reportData?.items || []);
        } catch (error) {
            console.error('Error fetching cancellation report:', error);
            setError('Failed to load cancellation report. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPage(0);
    };

    const resetFilters = () => {
        setFilters({
            startDate: '',
            endDate: '',
            period: 'daily',
            bookingSource: ''
        });
        setPage(0);
    };

    const handleExportClick = (event) => {
        setExportAnchorEl(event.currentTarget);
    };

    const handleExportClose = () => {
        setExportAnchorEl(null);
    };

    const handleExport = async (format) => {
        handleExportClose();
        setIsExporting(true);
        setError(null);
        try {
            // Force the API to return CSV, which we will convert in the browser
            const params = { ...filters, format: 'csv' };
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null || params[key] === undefined) delete params[key];
            });
            const res = await ReportService.exportCancellationReport(tenantId, params);
            
            // Extract the raw CSV text safely
            let csvText = '';
            if (res.data instanceof Blob) {
                csvText = await res.data.text();
            } else if (typeof res.data === 'string') {
                csvText = res.data;
            } else {
                csvText = JSON.stringify(res.data);
            }
            
            // Parse CSV into an array of arrays
            const rows = csvText.split('\n')
                .map(row => row.split(',').map(cell => cell.replace(/"/g, '').trim()))
                .filter(row => row.length > 0 && row.some(cell => cell !== ''));
                
            const fileName = `Cancellations_Report_${new Date().toISOString().split('T')[0]}`;
            
            if (format === 'csv') {
                const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${fileName}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            } else if (format === 'excel') {
                const ws = XLSX.utils.aoa_to_sheet(rows);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "Cancellations");
                XLSX.writeFile(wb, `${fileName}.xlsx`);
            } else if (format === 'pdf') {
                const doc = new jsPDF();
                const headers = rows[0] || [];
                const body = rows.slice(1) || [];
                
                doc.text("Cancellations Report", 14, 15);
                autoTable(doc, {
                    head: [headers],
                    body: body,
                    startY: 20,
                    styles: { fontSize: 8, cellPadding: 2 },
                    headStyles: { fillColor: [63, 81, 181] }
                });
                doc.save(`${fileName}.pdf`);
            }
        } catch (err) {
            console.error(`Error exporting cancellations report as ${format}:`, err);
            setError(`Failed to export ${format.toUpperCase()} report. Please try again.`);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Box>
            <Snackbar 
                open={!!error} 
                autoHideDuration={6000} 
                onClose={() => setError(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity="warning" onClose={() => setError(null)}>{error}</Alert>
            </Snackbar>

            <Paper sx={{ p: 2, mb: 3, borderRadius: '12px', border: '1px solid #edf1f6', bgcolor: '#ffffff', boxShadow: 'none' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Start Date"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={filters.startDate}
                            onChange={(e) => handleFilterChange('startDate', e.target.value)}
                            sx={standardFilterInputSx}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="End Date"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={filters.endDate}
                            onChange={(e) => handleFilterChange('endDate', e.target.value)}
                            sx={standardFilterInputSx}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl size="small" fullWidth>
                            <Select
                                value={filters.period}
                                displayEmpty
                                onChange={(e) => handleFilterChange('period', e.target.value)}
                                sx={standardFilterSelectSx}
                            >
                                <MenuItem value="daily">Daily</MenuItem>
                                <MenuItem value="weekly">Weekly</MenuItem>
                                <MenuItem value="monthly">Monthly</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl size="small" fullWidth>
                            <Select
                                value={filters.bookingSource}
                                displayEmpty
                                onChange={(e) => handleFilterChange('bookingSource', e.target.value)}
                                sx={standardFilterSelectSx}
                            >
                                <MenuItem value="">All Sources</MenuItem>
                                <MenuItem value="Direct">Direct</MenuItem>
                                <MenuItem value="OTA">OTA</MenuItem>
                                <MenuItem value="Corporate">Corporate</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                    {(filters.startDate !== '' || filters.endDate !== '' || filters.period !== 'daily' || filters.bookingSource !== '') && (
                        <Button variant="outlined" startIcon={<ReplayIcon sx={{ fontSize: 16 }} />} sx={standardResetButtonSx} onClick={resetFilters} disabled={isExporting}>Reset</Button>
                    )}
                    <Button 
                        variant="outlined" 
                        onClick={handleExportClick}
                        disabled={isExporting}
                        startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
                        endIcon={<KeyboardArrowDownIcon />}
                        sx={standardSecondaryButtonSx}
                    >
                        {isExporting ? 'Exporting...' : 'Export Report'}
                    </Button>
                    <Menu
                        anchorEl={exportAnchorEl}
                        open={Boolean(exportAnchorEl)}
                        onClose={handleExportClose}
                    >
                        <MenuItem onClick={() => handleExport('csv')}>Export as CSV</MenuItem>
                        <MenuItem onClick={() => handleExport('excel')}>Export as Excel</MenuItem>
                        <MenuItem onClick={() => handleExport('pdf')}>Export as PDF</MenuItem>
                    </Menu>
                </Box>
            </Paper>

            {cancelSummary && !isLoading && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' }}>
                            <Typography variant="h5" color="primary" fontWeight="bold">{cancelSummary.totalCancellations || 0}</Typography>
                            <Typography variant="body2" color="text.secondary">Total Cancellations</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' }}>
                            <Typography variant="h5" color="primary" fontWeight="bold">
                                {formatCurrency(cancelSummary.revenueLost || 0)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">Lost / Forfeited Revenue</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' }}>
                            <Typography variant="h5" color="primary" fontWeight="bold">
                                {formatCurrency(cancelSummary.revenueRecovered || 0)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">Revenue Recovered</Typography>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Booking ID / Guest</TableCell>
                            <TableCell>Scheduled Stay</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Source</TableCell>
                            <TableCell>Cancellation Reason</TableCell>
                            <TableCell align="right">Forfeited Value</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            Array.from(new Array(5)).map((_, idx) => (
                                <TableRow key={idx}>
                                    <TableCell><Skeleton animation="wave" /></TableCell>
                                    <TableCell><Skeleton animation="wave" /></TableCell>
                                    <TableCell><Skeleton animation="wave" /></TableCell>
                                    <TableCell><Skeleton animation="wave" /></TableCell>
                                    <TableCell><Skeleton animation="wave" /></TableCell>
                                    <TableCell><Skeleton animation="wave" /></TableCell>
                                </TableRow>
                            ))
                        ) : (
                            cancelData.length > 0 ? (
                                cancelData.map((row, idx) => (
                                    <TableRow key={idx} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold">{row.bookingId || row.id?.substring(0, 8) || '-'}</Typography>
                                            <Typography variant="caption" color="text.secondary">{row.guest?.name || '-'}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{row.checkIn ? new Date(row.checkIn).toLocaleDateString() : '-'}</Typography>
                                            <Typography variant="caption" color="text.secondary">to {row.checkOut ? new Date(row.checkOut).toLocaleDateString() : '-'}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={row.status || 'Cancelled'} 
                                                size="small" 
                                                color="error"
                                            />
                                        </TableCell>
                                        <TableCell>{row.bookingSource || '-'}</TableCell>
                                        <TableCell>{row.paymentStatus || 'No Show / Unspecified'}</TableCell>
                                        <TableCell align="right">
                                            {formatCurrency(row.cancellationFee || 0)}
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                        <Typography color="text.secondary">No cancellation records found for the selected criteria.</Typography>
                                    </TableCell>
                                </TableRow>
                            )
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={cancelData.length >= rowsPerPage ? -1 : cancelData.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[20, 50, 100]}
                />
            </TableContainer>
        </Box>
    );
}
